import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { useEffect } from 'react';
import { POST } from '../../../configs/api';
import { api, states } from '../../../utils/constants';
import { getActiveAdmins } from '../../../store/slices/usersSlice';
import { useSelector, useDispatch } from 'react-redux';
import Autocomplete from '@mui/material/Autocomplete';
import OptimizedCityAutocomplete from '../../../components/OptimizedCityAutocomplete';
import GooglePlacesAutocomplete from '../../../components/GooglePlacesAutocomplete';
import Checkbox from '@mui/material/Checkbox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { addClient, removeClientRequest } from '../../../store/slices/clientsSlice';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Draggable from 'react-draggable';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import InputAdornment from '@mui/material/InputAdornment';
import { useTheme } from '@mui/material/styles';
import useIsMobile from '../../../hooks/useIsMobile';
import ClientInvoicePatternField from './ClientInvoicePatternField';
import { isValidInvoicePattern, invoicePatternErrorMessage } from '../../../utils/invoicePattern';

function PaperComponent(props) {
  const nodeRef = React.useRef(null);
  return (
    <Draggable
      nodeRef={nodeRef}
      handle="#draggable-dialog-title"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper {...props} ref={nodeRef} />
    </Draggable>
  );
}
const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

// Phone formatting function
const formatPhoneNumber = (value) => {
  // Remove all non-numeric characters
  const phoneNumber = value.replace(/\D/g, '');
  
  // Format as (XXX) XXX-XXXX
  if (phoneNumber.length >= 6) {
    const areaCode = phoneNumber.substring(0, 3);
    const firstPart = phoneNumber.substring(3, 6);
    const secondPart = phoneNumber.substring(6, 10);
    return `(${areaCode}) ${firstPart}-${secondPart}`;
  } else if (phoneNumber.length >= 3) {
    const areaCode = phoneNumber.substring(0, 3);
    const firstPart = phoneNumber.substring(3);
    return `(${areaCode}) ${firstPart}`;
  } else if (phoneNumber.length > 0) {
    return `(${phoneNumber}`;
  }
  return phoneNumber;
};

// Zip code formatting function
const formatZipCode = (value) => {
  const zipCode = value.replace(/\D/g, '');
  if (zipCode.length > 5) {
    const firstPart = zipCode.substring(0, 5);
    const secondPart = zipCode.substring(5, 9);
    return `${firstPart}-${secondPart}`;
  }
  return zipCode;
};

const emptyErrors = { client_name: '', phone: '', city: '', state: '', zip_code: '', display_as: '', client_address: '', review_notes: '', invoice_pattern: '' };

export default function AcceptClientRequestDialog({ 
  setAcceptClientRequestDialogOpen, 
  acceptClientRequestDialogOpen, 
  selectedRequest 
}) {
  const theme = useTheme();
  const isSmallScreen = useIsMobile();
  const isDarkMode = theme.palette.mode === 'dark';
  const dispatch = useDispatch();
  const admins = useSelector(getActiveAdmins);
  const [showError, setShowError] = useState('');
  const [errors, setErrors] = useState(emptyErrors);
  const [clientData, setClientData] = useState({
    client_name: '',
    phone: '',
    city: '',
    state: '',
    zip_code: '',
    display_as: '',
    client_address: '',
    email: '',
    invoice_pattern: '',
    admins: [],
    review_notes: '',
    latitude: null,
    longitude: null
  });
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [loading, setLoading] = useState(false);

  // Initialize form data when dialog opens; by default include the user who requested the client in admins
  useEffect(() => {
    if (selectedRequest) {
      const requesterAdmin = (admins || []).find((a) => a.id === selectedRequest.requested_by);
      setClientData({
        client_name: selectedRequest.client_name || '',
        phone: selectedRequest.phone || '',
        city: selectedRequest.city || '',
        state: selectedRequest.state || '',
        zip_code: selectedRequest.zip_code || '',
        display_as: selectedRequest.display_as || '',
        client_address: selectedRequest.client_address || '',
        email: selectedRequest.email || '',
        invoice_pattern: selectedRequest.invoice_pattern || '',
        admins: requesterAdmin ? [requesterAdmin] : [],
        review_notes: selectedRequest.review_notes || '',
        latitude: selectedRequest.latitude ?? null,
        longitude: selectedRequest.longitude ?? null
      });
    }
  }, [selectedRequest, admins]);

  // Accept client request api
  const handleAcceptClientRequest = async () => {
    setLoading(true);
    setShowError('');
    setErrors(emptyErrors);
    let newErrors = {};
    (clientData.client_name.length < 3 || clientData.client_name.length > 254) && (newErrors.client_name = 'Client name must be between 3 and 254 characters');
    const phoneDigits = clientData.phone.replace(/\D/g, '');
    (phoneDigits.length < 10 || phoneDigits.length > 10) && (newErrors.phone = 'Please enter a valid 10-digit US phone number');
    (clientData.city.length < 2 || clientData.city.length > 254) && (newErrors.city = 'City must be between 2 and 254 characters');
    (clientData.state.length < 2 || clientData.state.length > 254) && (newErrors.state = 'State must be between 2 and 254 characters');
    const zipRegex = /^\d{5}(-\d{4})?$/;
    (!zipRegex.test(clientData.zip_code)) && (newErrors.zip_code = 'Please enter a valid US zip code (e.g., 12345 or 12345-6789)');
    (clientData.display_as.length < 3 || clientData.display_as.length > 254) && (newErrors.display_as = 'Display as must be between 2 and 254 characters');
    (clientData.client_address.length < 3 || clientData.client_address.length > 254) && (newErrors.client_address = 'Client address must be between 2 and 254 characters');
    (!isValidInvoicePattern(clientData.invoice_pattern)) && (newErrors.invoice_pattern = invoicePatternErrorMessage);
    setErrors(newErrors);
    if (Object.keys(newErrors).length === 0) {
      // Always include the requester in user_client_access (they get access to the client they requested)
      const adminIds = [...new Set([...clientData.admins.map((user) => user.id), selectedRequest.requested_by])];
      const { status, data, message } = await POST(api.acceptClientRequest, {}, { 
        ...clientData, 
        admins: adminIds,
        request_id: selectedRequest.id
      });
      if (status === 201) {
        setShowSuccessToast(true);
        dispatch(addClient({...data, admins: clientData.admins}));
        dispatch(removeClientRequest(selectedRequest.id));
        setAcceptClientRequestDialogOpen(false);
      } else if (status === 409) {
        const duplicateMessage = data?.message || message || 'Client name already exists.';
        if (/invoice pattern/i.test(duplicateMessage)) {
          setErrors({ ...emptyErrors, invoice_pattern: duplicateMessage });
        }
        setShowError(duplicateMessage);
      } else {
        setShowError(data?.message || message || 'Something went wrong, please try again later.');
      }
    }
    setLoading(false);
  };

  // Clear form errors
  useEffect(() => {
    setErrors(emptyErrors);
    setShowError('');
  }, [clientData]);

  // Handle place selection from Google Places Autocomplete
  const handlePlaceSelect = (addressData) => {
    const formattedZip = (addressData.zipCode && addressData.zipCode.trim())
      ? formatZipCode(addressData.zipCode)
      : '';
    setClientData({
      ...clientData,
      client_address: addressData.address || clientData.client_address,
      city: (addressData.city && addressData.city.trim()) ? addressData.city : '',
      state: (addressData.state && addressData.state.trim()) ? addressData.state : '',
      zip_code: formattedZip,
      latitude: addressData.latitude != null ? addressData.latitude : null,
      longitude: addressData.longitude != null ? addressData.longitude : null,
    });
  };

  // Common TextField sx prop
  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 1,
      backgroundColor: 'background.paper',
      '& fieldset': {
        borderColor: isDarkMode ? '#334155' : '#e0e0e0',
      },
      '&:hover fieldset': {
        borderColor: isDarkMode ? '#475569' : '#bdbdbd',
      },
      '&.Mui-focused fieldset': {
        borderColor: isDarkMode ? '#fff' : '#000',
      }
    },
    '& .MuiInputLabel-root': {
      fontSize: '0.875rem',
      fontWeight: 600,
      color: 'text.secondary',
      '&.Mui-focused': {
        color: 'text.primary',
      }
    },
    '& .MuiInputBase-input': {
      fontSize: '0.875rem',
      color: 'text.primary',
    },
    '& .MuiFormHelperText-root': {
      fontSize: '0.75rem',
    }
  };
  
  return (
    <Dialog
      open={acceptClientRequestDialogOpen}
      onClose={() => setAcceptClientRequestDialogOpen(false)}
      maxWidth="md"
      fullWidth
      fullScreen={isSmallScreen}
      PaperComponent={isSmallScreen ? undefined : PaperComponent}
      aria-labelledby="draggable-dialog-title"
      slotProps={{
        paper: {
          sx: {
            borderRadius: isSmallScreen ? 0 : 2,
            maxHeight: isSmallScreen ? '100%' : '90vh',
            backgroundColor: 'background.paper',
            m: isSmallScreen ? 0 : undefined,
          }
        }
      }}
    >
      {/* Header */}
      <Box id="draggable-dialog-title" sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between', 
        p: { xs: 2, md: 3 }, 
        borderBottom: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
        cursor: isSmallScreen ? 'default' : 'move',
        backgroundColor: 'background.paper',
      }}>
        <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary' }}>
          Accept Client Request
        </Typography>
        <IconButton onClick={() => setAcceptClientRequestDialogOpen(false)} sx={{ color: 'text.secondary' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 3, backgroundColor: 'background.paper' }}>
        {/* Error Message */}
        {showError && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" sx={{ color: 'error.main', fontWeight: 600 }}>
              {showError}
            </Typography>
          </Box>
        )}

        {/* Form Fields */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Client Name */}
          <TextField
            fullWidth
            label="Client Name"
            value={clientData.client_name || ''}
            onChange={(event) => setClientData({ ...clientData, client_name: event.target.value })}
            error={!!errors.client_name}
            helperText={errors.client_name}
            sx={textFieldSx}
          />

          {/* Display As */}
          <TextField
            fullWidth
            label="Display As"
            value={clientData.display_as || ''}
            onChange={(event) => setClientData({ ...clientData, display_as: event.target.value })}
            error={!!errors.display_as}
            helperText={errors.display_as}
            sx={textFieldSx}
          />

          <ClientInvoicePatternField
            value={clientData.invoice_pattern}
            onChange={(invoice_pattern) => setClientData({ ...clientData, invoice_pattern })}
            error={errors.invoice_pattern}
            textFieldSx={textFieldSx}
          />

          {/* Email */}
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={clientData.email || ''}
            onChange={(event) => setClientData({ ...clientData, email: event.target.value })}
            sx={textFieldSx}
          />

          {/* Admins */}
          <Autocomplete 
            multiple 
            id="admins-checkboxes" 
            options={admins || []} 
            getOptionLabel={(option) => option.email} 
            value={clientData.admins} 
            onChange={(_, value) => setClientData({ ...clientData, admins: value })}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                backgroundColor: 'background.paper',
                '& fieldset': {
                  borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                },
                '&:hover fieldset': {
                  borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                },
                '&.Mui-focused fieldset': {
                  borderColor: isDarkMode ? '#fff' : '#000',
                }
              },
              '& .MuiAutocomplete-listbox': {
                borderRadius: 2,
                backgroundColor: 'background.paper',
                border: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
                padding: 0,
                overflow: 'hidden',
              }
            }}
            renderOption={(props, option, { selected }) => {
              const { key, ...optionProps } = props;
              return (
                <li 
                  key={key} 
                  {...optionProps}
                  style={{
                    ...optionProps.style,
                    padding: '12px 16px',
                    margin: 0,
                    borderBottom: isDarkMode ? '1px solid #334155' : '1px solid rgba(0, 0, 0, 0.04)',
                    backgroundColor: selected 
                      ? (isDarkMode ? '#334155' : '#ececec')
                      : 'transparent',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                  onMouseEnter={(e) => {
                    if (!selected) {
                      e.currentTarget.style.backgroundColor = isDarkMode ? '#1e293b' : '#f5f5f5';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!selected) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  <Checkbox 
                    icon={icon} 
                    checkedIcon={checkedIcon} 
                    checked={selected}
                    sx={{ 
                      marginRight: 2,
                      padding: '6px',
                      '& .MuiSvgIcon-root': {
                        fontSize: '1.1rem',
                        color: selected ? 'primary.main' : 'text.secondary',
                        transition: 'all 0.2s ease-in-out',
                      },
                      '&:hover .MuiSvgIcon-root': {
                        color: 'primary.main',
                        transform: 'scale(1.1)',
                      }
                    }}
                  />
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontWeight: selected ? 600 : 500,
                      color: selected ? 'primary.main' : 'text.primary',
                      fontSize: '0.95rem',
                      transition: 'all 0.2s ease-in-out',
                    }}
                  >
                    {option.email}
                  </Typography>
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField 
                {...params} 
                label="Admins" 
                placeholder="Admins" 
                fullWidth 
                sx={textFieldSx}
              />
            )}
          />

          {/* Phone */}
          <TextField
            fullWidth
            label="Phone"
            value={clientData.phone || ''}
            onChange={(event) => {
              const formattedPhone = formatPhoneNumber(event.target.value);
              setClientData({ ...clientData, phone: formattedPhone });
            }}
            error={!!errors.phone}
            helperText={errors.phone}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span style={{ fontSize: '1.2rem' }}>🇺🇸</span>
                    <Typography sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.95rem' }}>
                      +1
                    </Typography>
                  </Box>
                </InputAdornment>
              ),
            }}
            placeholder="(555) 123-4567"
            sx={textFieldSx}
          />

          {/* Client Address */}
          <GooglePlacesAutocomplete
            label="Client Address"
            value={clientData.client_address || ''}
            onChange={(value) => setClientData({ ...clientData, client_address: value })}
            onPlaceSelect={handlePlaceSelect}
            error={!!errors.client_address}
            helperText={errors.client_address}
            placeholder=""
          />

          {/* City */}
          <OptimizedCityAutocomplete
            value={clientData.city || ''}
            onChange={(value) => setClientData({ ...clientData, city: value })}
            renderInput={(params) => (
              <TextField
                {...params}
                label="City"
                placeholder="Select or type city name"
                fullWidth
                error={!!errors.city}
                helperText={errors.city}
                sx={textFieldSx}
              />
            )}
          />

          {/* State */}
          <Autocomplete
            options={Object.keys(states)}
            value={clientData.state || ''}
            onChange={(event, value) => setClientData({ ...clientData, state: value })}
            getOptionLabel={(option) => option ? `${states[option]} - ${option}` : ''}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                backgroundColor: 'background.paper',
                '& fieldset': {
                  borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                },
                '&:hover fieldset': {
                  borderColor: isDarkMode ? '#475569' : '#bdbdbd',
                },
                '&.Mui-focused fieldset': {
                  borderColor: isDarkMode ? '#fff' : '#000',
                }
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="State"
                placeholder="Select state"
                fullWidth
                error={!!errors.state}
                helperText={errors.state}
                sx={textFieldSx}
              />
            )}
          />

          {/* Zip Code */}
          <TextField
            fullWidth
            label="Zip Code"
            value={clientData.zip_code || ''}
            onChange={(event) => {
              const formattedZip = formatZipCode(event.target.value);
              setClientData({ ...clientData, zip_code: formattedZip });
            }}
            error={!!errors.zip_code}
            helperText={errors.zip_code}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span style={{ fontSize: '1.2rem' }}>🇺🇸</span>
                  </Box>
                </InputAdornment>
              ),
            }}
            placeholder="12345 or 12345-6789"
            sx={textFieldSx}
          />

          {/* Review Notes */}
          <TextField
            fullWidth
            label="Review Notes"
            value={clientData.review_notes || ''}
            onChange={(event) => setClientData({ ...clientData, review_notes: event.target.value })}
            error={!!errors.review_notes}
            helperText={errors.review_notes}
            sx={textFieldSx}
          />
        </Box>
      </DialogContent>

      {/* Dialog Actions */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'flex-end',
        gap: 2,
        p: 3, 
        borderTop: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
        backgroundColor: 'background.paper',
      }}>
        <Button 
          onClick={() => setAcceptClientRequestDialogOpen(false)}
          variant="outlined"
          sx={{ 
            borderColor: isDarkMode ? '#334155' : '#e0e0e0',
            color: 'text.primary',
            textTransform: 'none',
            fontSize: '0.875rem',
            fontWeight: 600,
            px: 3,
            py: 1.25,
            borderRadius: 1,
            '&:hover': {
              borderColor: isDarkMode ? '#475569' : '#bdbdbd',
              backgroundColor: isDarkMode ? '#1e293b' : '#fafafa',
            }
          }}
        >
          Cancel
        </Button>
        
        <Button 
          variant="contained"
          disabled={loading}
          onClick={handleAcceptClientRequest}
          sx={{ 
            backgroundColor: isDarkMode ? '#3b82f6' : '#000',
            color: 'white',
            textTransform: 'none',
            fontSize: '0.875rem',
            fontWeight: 600,
            px: 3,
            py: 1.25,
            borderRadius: 1,
            '&:hover': { 
              backgroundColor: isDarkMode ? '#2563eb' : '#333' 
            },
            '&:disabled': {
              backgroundColor: isDarkMode ? '#1e293b' : '#e0e0e0',
              color: isDarkMode ? '#64748b' : '#9e9e9e',
            }
          }}
        >
          Accept Client Request
        </Button>
      </Box>
      {/* Success Toast */}
      <Snackbar
        open={showSuccessToast}
        autoHideDuration={3000}
        onClose={() => setShowSuccessToast(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowSuccessToast(false)}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          Client request accepted successfully
        </Alert>
      </Snackbar>
    </Dialog>
  );
}
